/**
 * Turn actions (submit, exchange, pass, resign, claim win, rematch) with their
 * optimistic "pending" state and feedback. All of them read the latest store
 * state at call time, so they can be bound to buttons and shortcuts alike.
 */
import { CELEBRATE_BIG_MOVE, CELEBRATE_GOOD_MOVE, cellName } from '@wordloom/engine';
import type { Coord, GameView, Placement, RoomView } from '@wordloom/engine';
import { sfx } from '../audio/sfx';
import * as api from '../net/api';
import { isTransportError, type ClientErrorPayload } from '../net/errors';
import { recoverSession } from '../net/session';
import { useDraftStore } from '../state/draftStore';
import { useRoomStore } from '../state/roomStore';
import { useUiStore } from '../state/uiStore';
import { dictCache } from './dict';
import { draftCoords } from './draft';
import { seatName } from './events';
import { evaluateDraft, summarizePreview } from './preview';
import { reasons } from './selectors';
import { sentence } from './text';

interface MyTurn {
  view: RoomView;
  game: GameView;
}

/** The current game when it is my turn and nothing is in flight; null otherwise. */
function myTurn(): MyTurn | null {
  const view = useRoomStore.getState().view;
  const game = view?.game;
  if (!view || !game || game.status !== 'playing' || game.currentSeat !== view.you) return null;
  if (useDraftStore.getState().pending) return null;
  return { view, game };
}

/**
 * The game state an intent is decided against: which game, which turn, and
 * whether it is still running. Taken when the intent is sent and compared when
 * a refusal comes back.
 */
function decisionPoint(): string {
  const view = useRoomStore.getState().view;
  return `${view?.gameNumber ?? '-'}:${view?.game?.turnNumber ?? '-'}:${view?.game?.status ?? '-'}`;
}

/** The toast that says why an action can't run (each one replaces the last). */
export const EXPLAIN_TOAST_ID = 'disabled-action';

/**
 * A move the rules or the dictionary refuse. The move preview already says
 * why, in the same words (the one source of truth: it follows the draft, so
 * it can never contradict it), so no toast repeats it and outlives the next
 * draft change over the preview, the scoreboard or a dialog. Instead: the
 * buzz, the offending squares ringed and the preview shaken and scrolled into
 * view (draftStore.shakeNonce, see MovePreview), and the reason said to
 * screen readers.
 */
function refuseMove(reason: string, cells: readonly Coord[]): void {
  const ui = useUiStore.getState();
  sfx.invalid();
  useDraftStore.getState().markInvalid([...cells]);
  ui.dismissToast(EXPLAIN_TOAST_ID);
  ui.announce(reason, 'assertive');
}

/**
 * Feedback for a rejected intent. Transport failures never shake the board.
 * `sentAt` is the decisionPoint() when the intent left: if the game moved on
 * meanwhile (the turn timer fired, the opponent resigned or accepted the
 * rematch), the race was settled and the new view already told the story, so
 * the refusal is dropped without a sound, red rings or a toast.
 * `inPreview`: the preview now shows this refusal (a word the dictionary
 * rejected), so it is refused like a locally invalid move, without a toast.
 */
function reportError(error: ClientErrorPayload, sentAt: string, cells?: Coord[], inPreview = false): void {
  const ui = useUiStore.getState();
  if (isTransportError(error)) {
    ui.toast(error.message, { kind: 'warning', id: 'transport' });
    return;
  }
  recoverSession(error);
  if (decisionPoint() !== sentAt) return;
  if (inPreview) {
    refuseMove(error.message, cells ?? []);
    return;
  }
  sfx.invalid();
  if (cells) useDraftStore.getState().markInvalid(cells);
  ui.toast(error.message, { kind: 'error' });
}

/**
 * Why an action can't run right now: the same reason as the disabled
 * button's tooltip. Used by disabled buttons and by the keyboard paths that
 * reach the same dead end. A gate (not your turn, no tiles) is a short info
 * toast (announced too); an invalid move is refused like the server refuses
 * one, pointing at the preview that already explains it (refuseMove).
 */
export function explainDisabled(reason: string, cells?: readonly Coord[], kind: 'info' | 'error' = 'info'): void {
  if (kind === 'error') {
    refuseMove(reason, cells ?? []);
    return;
  }
  sfx.invalid();
  if (cells?.length) useDraftStore.getState().markInvalid([...cells]);
  useUiStore.getState().toast(reason, { kind, id: EXPLAIN_TOAST_ID, duration: 2600 });
}

/** Submit the draft. Blanks without a letter open the picker first. */
export async function submitDraft(): Promise<void> {
  const turn = myTurn();
  const draft = useDraftStore.getState();
  if (!turn) {
    // Off-turn Enter explains itself like the disabled Play button does. Game
    // over (the dialog says it) and a move in flight (the spinner) stay quiet.
    const view = useRoomStore.getState().view;
    const game = view?.game;
    if (view && game?.status === 'playing' && game.currentSeat !== view.you) {
      explainDisabled(reasons.opponentsTurn(seatName(view, game.currentSeat)));
    }
    return;
  }
  if (draft.placements.length === 0) {
    explainDisabled(reasons.noTiles);
    return;
  }
  const { view, game } = turn;

  const rack = new Map(game.rack.map((t) => [t.id, t]));
  const unlettered = draft.placements.find((p) => rack.get(p.tileId)?.blank && !p.letter);
  if (unlettered) {
    draft.setPicker({ tileId: unlettered.tileId, row: unlettered.row, col: unlettered.col, mode: 'place' });
    useUiStore.getState().announce(`Choose a letter for the blank on ${cellName(unlettered.row, unlettered.col)}.`);
    return;
  }

  // A move already known to be invalid (the rules, or a word the dictionary
  // rejected) is refused here exactly as the disabled Play button refuses it:
  // same reason, same rings, no round trip.
  const local = summarizePreview(evaluateDraft(game.board, draft.placements, game.rack), (w) => dictCache.status(w));
  if (local.state === 'invalid') {
    explainDisabled(local.reason ?? 'That move isn’t valid.', local.badCells, 'error');
    return;
  }

  const placements: Placement[] = draft.placements.map((p) =>
    p.letter ? { tileId: p.tileId, row: p.row, col: p.col, letter: p.letter } : { tileId: p.tileId, row: p.row, col: p.col },
  );
  const cells = draftCoords(draft.placements);
  draft.select(null);
  draft.setPicker(null);
  draft.setPending(true);
  sfx.submit();
  const sentAt = decisionPoint();
  const res = await api.play(game.turnNumber, placements);
  useDraftStore.getState().setPending(false);

  if (!res.ok) {
    const rejected = res.error.invalidWords ?? [];
    // Seeding the verdict puts the refusal in the preview, in the same words.
    for (const w of rejected) dictCache.seed(w, false);
    reportError(res.error, sentAt, res.error.cells?.length ? res.error.cells : cells, rejected.length > 0);
    return;
  }

  for (const w of res.words) dictCache.seed(w.word, true);
  const kind = res.bingo
    ? 'bingo'
    : res.score >= CELEBRATE_BIG_MOVE
      ? 'big'
      : res.score >= CELEBRATE_GOOD_MOVE
        ? 'good'
        : 'score';
  const ui = useUiStore.getState();
  ui.celebrate({ kind, score: res.score, seat: view.you, cells });
  sfx.score(res.score);
  if (res.bingo) sfx.bingo();
  const main = res.words[0]?.word ?? 'your word';
  ui.announce(sentence(`You played ${main} for ${res.score}${res.bingo ? ' — bingo!' : ''}`));
  if (ui.onboardingStep !== null) ui.dismissOnboarding();
}

export async function exchangeTiles(tileIds: string[]): Promise<boolean> {
  const turn = myTurn();
  if (!turn || tileIds.length === 0) return false;
  const draft = useDraftStore.getState();
  draft.recallAll();
  draft.setPending(true);
  const sentAt = decisionPoint();
  const res = await api.exchange(turn.game.turnNumber, tileIds);
  useDraftStore.getState().setPending(false);
  if (!res.ok) {
    reportError(res.error, sentAt);
    return false;
  }
  sfx.shuffle();
  const n = tileIds.length;
  useUiStore.getState().toast(`Exchanged ${n} ${n === 1 ? 'tile' : 'tiles'}`, { kind: 'success' });
  return true;
}

export async function passTurn(): Promise<boolean> {
  const turn = myTurn();
  if (!turn) return false;
  useDraftStore.getState().setPending(true);
  const sentAt = decisionPoint();
  const res = await api.pass(turn.game.turnNumber);
  useDraftStore.getState().setPending(false);
  if (!res.ok) {
    reportError(res.error, sentAt);
    return false;
  }
  useUiStore.getState().announce('You passed.');
  return true;
}

export async function resignGame(): Promise<boolean> {
  const view = useRoomStore.getState().view;
  if (!view?.game || view.game.status !== 'playing') return false;
  const sentAt = decisionPoint();
  const res = await api.resign(view.gameNumber);
  if (!res.ok) {
    reportError(res.error, sentAt);
    return false;
  }
  return true;
}

export async function claimWin(): Promise<boolean> {
  const view = useRoomStore.getState().view;
  if (!view?.game || view.game.status !== 'playing') return false;
  const sentAt = decisionPoint();
  const res = await api.claimWin(view.gameNumber);
  if (!res.ok) {
    reportError(res.error, sentAt);
    return false;
  }
  return true;
}

export async function voteRematch(want: boolean): Promise<boolean> {
  const sentAt = decisionPoint();
  const res = await api.rematch(want);
  if (!res.ok) {
    reportError(res.error, sentAt);
    return false;
  }
  return true;
}
